'use client';

import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGSAP } from '@gsap/react';
import Hls from 'hls.js';
import initAnimations from './animations/3d-generate-animation';
import { useLocale } from '@/components/providers/context/locale-context';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';
import useViewportActivation from '@/app/lib/hooks/use-viewport-activation';
const COVER_IMAGE_SRC = `${process.env.NEXT_PUBLIC_IMAGE_PUBLIC_BASE_URL}/home/img-3d-generate-cover.png`;

function RingLoader() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="hsla(0, 0%, 100%, 1.00)"
            xmlns="http://www.w3.org/2000/svg"
            className="h-full w-full"
            aria-hidden="true"
        >
            <path
                d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"
                opacity=".25"
            />
            <path d="M10.72,19.9a8,8,0,0,1-6.5-9.79A7.77,7.77,0,0,1,10.4,4.16a8,8,0,0,1,9.49,6.52A1.54,1.54,0,0,0,21.38,12h.13a1.37,1.37,0,0,0,1.38-1.54,11,11,0,1,0-12.7,12.39A1.54,1.54,0,0,0,12,21.34h0A1.47,1.47,0,0,0,10.72,19.9Z">
                <animateTransform
                    attributeName="transform"
                    type="rotate"
                    dur="0.75s"
                    values="0 12 12;360 12 12"
                    repeatCount="indefinite"
                />
            </path>
        </svg>
    );
}

function ThreeDGenerate() {
    const { t } = useTranslation('three-d-generate');
    const locale = useLocale();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLDivElement>(null);
    const colorfulGlowRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const batchImageContainerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const videoLoadingRef = useRef<HTMLDivElement>(null);
    const coverImageRef = useRef<HTMLImageElement>(null);
    const [coverImageLoaded, setCoverImageLoaded] = useState(false);
    const { ref: activationRef, isActive: shouldLoadVideo } =
        useViewportActivation<HTMLDivElement>({
            rootMargin: '400px 0px',
        });

    const setWrapperRefs = (node: HTMLDivElement | null) => {
        wrapperRef.current = node;
        activationRef.current = node;
    };

    useGSAP(
        () => {
            return initAnimations(
                wrapperRef.current!,
                rootRef.current!,
                inputRef.current!,
                t('input.text'),
                buttonRef.current!,
                batchImageContainerRef.current!,
                videoRef.current!,
                videoLoadingRef.current!,
            );
        },
        { scope: rootRef },
    );

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        let hls: Hls | null = null;

        if (!shouldLoadVideo) {
            video.pause();
            video.removeAttribute('src');
            video.load();
            return;
        }

        const src = `${process.env.NEXT_PUBLIC_VIDEO_BASE_URL}/home/video-3d-generated-hls/index.m3u8`;

        if (video.canPlayType('application/vnd.apple.mpegurl')) {
            // Safari 原生支持 HLS
            video.src = src;
        } else if (Hls.isSupported()) {
            hls = new Hls();
            hls.loadSource(src);
            hls.attachMedia(video);
        }

        return () => {
            if (hls) {
                hls.destroy();
            }
            video.pause();
            video.removeAttribute('src');
            video.load();
        };
    }, [shouldLoadVideo]);

    useEffect(() => {
        setCoverImageLoaded(false);

        const image = new window.Image();
        image.src = COVER_IMAGE_SRC;

        if (image.complete && image.naturalWidth > 0) {
            setCoverImageLoaded(true);
            return;
        }

        const handleLoad = () => setCoverImageLoaded(true);
        const handleError = () => setCoverImageLoaded(true);

        image.addEventListener('load', handleLoad);
        image.addEventListener('error', handleError);

        return () => {
            image.removeEventListener('load', handleLoad);
            image.removeEventListener('error', handleError);
        };
    }, []);

    useEffect(() => {
        const image = coverImageRef.current;
        if (!image) {
            return;
        }

        if (image.complete && image.naturalWidth > 0) {
            setCoverImageLoaded(true);
        }
    }, []);

    return (
        <div
            ref={setWrapperRefs}
            className="relative bg-black"
            style={{ height: 'calc(var(--homepage-vh, 100svh) * 1.5)' }}
        >
            <section
                id="three-d-generate"
                ref={rootRef}
                className="sticky top-0 flex w-full max-w-full flex-col bg-black"
                style={{ height: 'var(--homepage-vh, 100svh)' }}
            >
                <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center">
                    <div
                        id="three-d-generate-image"
                        ref={batchImageContainerRef}
                        className="tablet:max-w-[87.5vw] desktop:max-w-[1200px] pointer-events-auto w-[87.5vw] max-w-[480px] origin-center"
                    >
                        <div className="tablet:gap-[48px] desktop:gap-[60px] j-h4 tablet:j-h3 flex w-full flex-col gap-[32px] text-white">
                            <div className="tablet:items-center tablet:text-center flex flex-col items-start justify-center text-start">
                                <div>{t('genrationHeading.line1')}</div>
                                <div
                                    style={{
                                        background:
                                            'var(---Linear, linear-gradient(90deg, #765EFF 0%, #FFCFAA 100%))',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    {t('genrationHeading.line2')}
                                    {t('genrationHeading.line2Suffix', '') && (
                                        <span className="whitespace-nowrap">
                                            {t('genrationHeading.line2Suffix')}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {/* wrapper：承载尺寸 + relative 定位，光晕作为卡片兄弟节点 */}
                            <div
                                style={{ aspectRatio: '1200/639' }}
                                className="tablet:w-auto tablet:h-[60svh] tablet:max-w-full relative mx-auto w-[87.5vw]"
                            >
                                {/* 彩色模糊光晕层 —— 卡片的兄弟节点，不会被 video 覆盖 */}
                                <div
                                    style={{
                                        background:
                                            'linear-gradient(90deg, #c1b1ed 0%, #7f62d3 23.5%, #537cf7 53.15%, #e356fd 77.87%, #fbde7a 100%)',
                                        filter: 'blur(24px)',
                                    }}
                                    className="pointer-events-none absolute -inset-[4px] opacity-50"
                                />

                                {/* 卡片：渐变边框，absolute inset-0 填满 wrapper */}
                                <div
                                    style={{
                                        backgroundClip:
                                            'padding-box, border-box',
                                        backgroundOrigin:
                                            'padding-box, border-box',
                                        backgroundImage:
                                            'linear-gradient(to right, #21252d, #21252d), linear-gradient(90deg, #c1b1ed 0%, #7f62d3 23.5%, #537cf7 53.15%, #e356fd 77.87%, #fbde7a 100%)',
                                    }}
                                    className="tablet:border-[1.5px] tablet:rounded-[16px] absolute inset-0 overflow-hidden rounded-[8px] border-[1px] border-transparent"
                                >
                                    <div
                                        ref={videoLoadingRef}
                                        className="tablet:rounded-[16px] pointer-events-none absolute inset-0 z-10 rounded-[8px] bg-transparent opacity-0"
                                    >
                                        <div className="absolute bottom-[8px] right-[8px] h-[20px] w-[20px] text-black min-[735px]:bottom-[16px] min-[735px]:right-[16px] min-[735px]:h-[32px] min-[735px]:w-[32px]">
                                            <RingLoader />
                                        </div>
                                    </div>

                                    {!coverImageLoaded && (
                                        <div className="tablet:rounded-[16px] pointer-events-none absolute inset-0 z-[1] overflow-hidden rounded-[8px] bg-white/5 before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.6s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent" />
                                    )}
                                    <img
                                        id="three-d-generate-img"
                                        ref={coverImageRef}
                                        src={COVER_IMAGE_SRC}
                                        alt="3d generate"
                                        onLoad={() => setCoverImageLoaded(true)}
                                        onError={() =>
                                            setCoverImageLoaded(true)
                                        }
                                        className={
                                            coverImageLoaded
                                                ? 'tablet:rounded-[16px] absolute inset-0 h-full w-full rounded-[8px] object-cover'
                                                : 'tablet:rounded-[16px] absolute inset-0 h-full w-full rounded-[8px] object-cover opacity-0'
                                        }
                                    />

                                    <video
                                        id="three-d-generate-video"
                                        ref={videoRef}
                                        muted
                                        loop
                                        playsInline
                                        poster={COVER_IMAGE_SRC}
                                        className="tablet:rounded-[16px] absolute left-0 top-0 h-full w-full rounded-[8px] object-cover opacity-0"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    id="three-d-generate-input"
                    ref={inputRef}
                    className={`tablet:gap-[60px] desktop:gap-[80px] absolute left-0 top-0 z-20 flex h-full w-full max-w-full flex-col items-center justify-center gap-[40px]`}
                >
                    <div
                        className={clsx(
                            'j-h4 tablet:j-h3 tablet:items-center tablet:max-w-[600px] desktop:max-w-[800px] tablet:justify-center flex w-[87.5vw] max-w-[480px] items-start justify-start text-white',
                            {
                                'tablet:flex-row flex-col': locale === 'zh',
                                'flex-col': locale !== 'zh',
                            },
                        )}
                    >
                        <div>{t('inputHeading.line1')}</div>
                        <div
                            style={{
                                background:
                                    'var(---Linear, linear-gradient(90deg, #765EFF 0%, #FFCFAA 100%))',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            {t('inputHeading.line2')}
                        </div>
                    </div>
                    <div className="tablet:max-w-[600px] desktop:max-w-[800px] relative flex w-[87.5vw] max-w-[480px] items-center justify-center">
                        {/* 外发光包裹层 */}
                        <div className="relative w-full">
                            {/* 彩色模糊光晕层 */}
                            <div
                                ref={colorfulGlowRef}
                                style={{
                                    opacity:
                                        'var(--three-d-generate-input-opacity)',
                                    background:
                                        'linear-gradient(90deg, #c1b1ed 0%, #7f62d3 23.5%, #537cf7 53.15%, #e356fd 77.87%, #fbde7a 100%)',
                                    filter: 'blur(18px)',
                                }}
                                className="absolute -inset-[2px] rounded-[14px] opacity-60"
                            />
                            {/* 输入框主体 */}
                            <div
                                style={{
                                    opacity:
                                        'var(--three-d-generate-input-opacity)',
                                    backgroundClip: 'padding-box, border-box',
                                    backgroundOrigin: 'padding-box, border-box',
                                    backgroundImage:
                                        'linear-gradient(to right, #21252d, #21252d), linear-gradient(90deg, #c1b1ed 0%, #7f62d3 23.5%, #537cf7 53.15%, #e356fd 77.87%, #fbde7a 100%)',
                                }}
                                className="tablet:border-[1.5px] relative flex w-full flex-col rounded-[16px] border-[1px] border-transparent bg-[#21252d] shadow-lg shadow-black/40"
                            >
                                {/* 文本区域 */}
                                <div
                                    id="three-d-generate-input-text"
                                    className={clsx(
                                        'j-t2 tablet:px-[18px] tablet:py-[12px] min-h-[48px] px-[18px] py-[12px] text-left text-[rgba(255,255,255,0.86)]',
                                    )}
                                ></div>

                                {/* 底部工具栏 */}
                                <div className="flex items-center justify-between px-[8px] pb-[8px]">
                                    {/* 左侧：图标按钮组 + 分隔线 + 模型名 */}
                                    <div className="flex items-center gap-[4px]">
                                        {/* Prompt 图标 */}
                                        <button className="flex h-[32px] w-[32px] items-center justify-center rounded-[8px] transition-colors hover:bg-white/10">
                                            <svg
                                                width="32"
                                                height="32"
                                                viewBox="0 0 32 32"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M20.2 7H11.8C10.527 7 9.30606 7.50571 8.40589 8.40589C7.50571 9.30606 7 10.527 7 11.8V20.2C7 21.473 7.50571 22.6939 8.40589 23.5941C9.30606 24.4943 10.527 25 11.8 25H20.2C21.473 25 22.6939 24.4943 23.5941 23.5941C24.4943 22.6939 25 21.473 25 20.2V11.8C25 10.527 24.4943 9.30606 23.5941 8.40589C22.6939 7.50571 21.473 7 20.2 7ZM16.5 21H11.5C11.2348 21 10.9804 20.8946 10.7929 20.7071C10.6054 20.5196 10.5 20.2652 10.5 20C10.5 19.7348 10.6054 19.4804 10.7929 19.2929C10.9804 19.1054 11.2348 19 11.5 19H16.5C16.7652 19 17.0196 19.1054 17.2071 19.2929C17.3946 19.4804 17.5 19.7348 17.5 20C17.5 20.2652 17.3946 20.5196 17.2071 20.7071C17.0196 20.8946 16.7652 21 16.5 21ZM20.5 17H11.5C11.2348 17 10.9804 16.8946 10.7929 16.7071C10.6054 16.5196 10.5 16.2652 10.5 16C10.5 15.7348 10.6054 15.4804 10.7929 15.2929C10.9804 15.1054 11.2348 15 11.5 15H20.5C20.7652 15 21.0196 15.1054 21.2071 15.2929C21.3946 15.4804 21.5 15.7348 21.5 16C21.5 16.2652 21.3946 16.5196 21.2071 16.7071C21.0196 16.8946 20.7652 17 20.5 17ZM20.5 13H11.5C11.2348 13 10.9804 12.8946 10.7929 12.7071C10.6054 12.5196 10.5 12.2652 10.5 12C10.5 11.7348 10.6054 11.4804 10.7929 11.2929C10.9804 11.1054 11.2348 11 11.5 11H20.5C20.7652 11 21.0196 11.1054 21.2071 11.2929C21.3946 11.4804 21.5 11.7348 21.5 12C21.5 12.2652 21.3946 12.5196 21.2071 12.7071C21.0196 12.8946 20.7652 13 20.5 13Z"
                                                    fill="white"
                                                    fillOpacity="0.66"
                                                />
                                            </svg>
                                        </button>
                                        {/* P 图标 */}
                                        <button className="flex h-[32px] w-[32px] items-center justify-center rounded-[8px] transition-colors hover:bg-white/10">
                                            <svg
                                                width="32"
                                                height="32"
                                                viewBox="0 0 32 32"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M16 6C17.3132 6 18.6139 6.2582 19.8271 6.76074C21.0404 7.26329 22.1427 8.00015 23.0713 8.92871C23.9998 9.85727 24.7367 10.9596 25.2393 12.1729C25.7418 13.3861 26 14.6868 26 16C26 18.6522 24.9467 21.1959 23.0713 23.0713C21.1959 24.9467 18.6522 26 16 26C13.3478 26 10.8041 24.9467 8.92871 23.0713C7.05335 21.1959 6 18.6522 6 16C6 13.3478 7.05335 10.8041 8.92871 8.92871C10.8041 7.05335 13.3478 6 16 6ZM11 10.5C10.7348 10.5 10.4805 10.6054 10.293 10.793C10.1054 10.9805 10 11.2348 10 11.5V20.5C10 20.7652 10.1054 21.0195 10.293 21.207C10.4805 21.3946 10.7348 21.5 11 21.5C11.2652 21.5 11.5195 21.3946 11.707 21.207C11.8946 21.0195 12 20.7652 12 20.5V17.5H14.2998C15.228 17.5 16.118 17.1309 16.7744 16.4746C17.4308 15.8182 17.7998 14.9283 17.7998 14C17.7972 13.0726 17.4273 12.1841 16.7715 11.5283C16.1157 10.8726 15.2272 10.5026 14.2998 10.5H11ZM19 16.5C18.7348 16.5 18.4805 16.6054 18.293 16.793C18.1054 16.9805 18 17.2348 18 17.5C18 17.7652 18.1054 18.0195 18.293 18.207C18.4805 18.3946 18.7348 18.5 19 18.5H22C22.2652 18.5 22.5195 18.3946 22.707 18.207C22.8946 18.0195 23 17.7652 23 17.5C23 17.2348 22.8946 16.9805 22.707 16.793C22.5195 16.6054 22.2652 16.5 22 16.5H19ZM14.2998 12.5C14.6976 12.5 15.0791 12.6582 15.3604 12.9395C15.6417 13.2208 15.7998 13.6022 15.7998 14C15.7998 14.3978 15.6417 14.7792 15.3604 15.0605C15.0791 15.3418 14.6976 15.5 14.2998 15.5H12V12.5H14.2998Z"
                                                    fill="white"
                                                    fillOpacity="0.24"
                                                />
                                            </svg>
                                        </button>
                                        {/* 图片图标 */}
                                        <button className="flex h-[32px] w-[32px] items-center justify-center rounded-[8px] transition-colors hover:bg-white/10">
                                            <svg
                                                width="32"
                                                height="32"
                                                viewBox="0 0 32 32"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M20.2 7H11.8C10.527 7 9.30606 7.50571 8.40589 8.40589C7.50571 9.30606 7 10.527 7 11.8V20.2C7 21.473 7.50571 22.6939 8.40589 23.5941C9.30606 24.4943 10.527 25 11.8 25H20.2C21.473 25 22.6939 24.4943 23.5941 23.5941C24.4943 22.6939 25 21.473 25 20.2V11.8C25 10.527 24.4943 9.30606 23.5941 8.40589C22.6939 7.50571 21.473 7 20.2 7ZM19.51 10.7C19.9847 10.7 20.44 10.8886 20.7757 11.2243C21.1114 11.56 21.3 12.0153 21.3 12.49C21.3 12.9647 21.1114 13.42 20.7757 13.7557C20.44 14.0914 19.9847 14.28 19.51 14.28C19.0392 14.2674 18.5913 14.0747 18.2583 13.7417C17.9253 13.4087 17.7326 12.9608 17.72 12.49C17.7159 12.2538 17.7595 12.0192 17.848 11.8002C17.9365 11.5812 18.0682 11.3823 18.2352 11.2152C18.4023 11.0482 18.6012 10.9165 18.8202 10.828C19.0392 10.7395 19.2738 10.6959 19.51 10.7ZM20.2 23H11.8C11.0582 22.9974 10.3475 22.7015 9.82301 22.177C9.29848 21.6525 9.00264 20.9418 9 20.2V18.7L11.7 16C11.8078 15.8817 11.939 15.7873 12.0854 15.7226C12.2318 15.658 12.39 15.6246 12.55 15.6246C12.71 15.6246 12.8682 15.658 13.0146 15.7226C13.161 15.7873 13.2922 15.8817 13.4 16L20.4 23H20.2ZM21.91 22.41L18.5 19L19.7 17.7C19.811 17.6036 19.953 17.5505 20.1 17.5505C20.247 17.5505 20.389 17.6036 20.5 17.7L23 20.2C22.9982 20.627 22.899 21.0479 22.7102 21.4309C22.5213 21.8138 22.2477 22.1486 21.91 22.41Z"
                                                    fill="white"
                                                    fillOpacity="0.66"
                                                />
                                            </svg>
                                        </button>

                                        {/* 分隔线 */}
                                        <div className="bg-white/16 mx-[4px] h-[16px] w-[4px] rounded-full" />

                                        {/* 模型选择 */}
                                        <button className="flex items-center gap-[8px] rounded-[8px] px-[10px] py-[6px] transition-colors hover:bg-white/10">
                                            <img
                                                src={getStaticAssetUrl(
                                                    '/home/icon-terra-3.webp',
                                                )}
                                                alt="model"
                                                className="h-[20px] w-[20px] object-contain"
                                            />
                                            <span className="tablet:block font-inter hidden whitespace-nowrap text-[14px] leading-[20px] text-[rgba(255,255,255,0.66)]">
                                                Terra-3
                                            </span>
                                        </button>
                                    </div>

                                    {/* 右侧：字数 + 设置 + 分隔线 + 发送按钮 */}
                                    <div className="flex items-center gap-[8px]">
                                        {/* 字数统计 */}
                                        <span className="font-inter whitespace-nowrap text-[12px] text-[rgba(255,255,255,0.36)]">
                                            &nbsp;
                                        </span>
                                        {/* 设置图标 */}
                                        <button className="flex h-[32px] w-[32px] items-center justify-center rounded-[8px] transition-colors hover:bg-white/10">
                                            <svg
                                                width="32"
                                                height="32"
                                                viewBox="0 0 32 32"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M17.1804 6.69989C17.4878 6.72816 17.7733 6.87133 17.9802 7.10028C18.1101 7.1879 18.2149 7.30783 18.2849 7.44794C18.3549 7.58805 18.3885 7.74366 18.3806 7.90009C18.3615 8.13467 18.4193 8.36949 18.5457 8.56806C18.6719 8.7663 18.8596 8.91768 19.0798 8.9997C19.302 9.0826 19.5441 9.09889 19.7752 9.0456C20.0063 8.99226 20.2172 8.87182 20.3806 8.69989C20.4883 8.58181 20.6192 8.48713 20.7654 8.42255C20.9118 8.35791 21.0702 8.32489 21.2302 8.32489C21.3902 8.3249 21.5487 8.35791 21.6951 8.42255C21.8413 8.48714 21.9722 8.58181 22.0798 8.69989L23.4802 10.1003C23.5983 10.2079 23.693 10.3389 23.7576 10.485C23.8222 10.6314 23.8552 10.79 23.8552 10.9499C23.8552 11.1099 23.8222 11.2684 23.7576 11.4147C23.6929 11.5611 23.5985 11.6927 23.4802 11.8005C23.2934 11.9533 23.1624 12.1639 23.1082 12.3991C23.0539 12.6343 23.0794 12.8811 23.1804 13.1003C23.2798 13.3082 23.4352 13.4844 23.6297 13.6081C23.8241 13.7318 24.0496 13.7986 24.28 13.8005C24.5755 13.793 24.8622 13.9004 25.0798 14.1003C25.1864 14.2214 25.2622 14.3664 25.3005 14.5231C25.3388 14.6797 25.3391 14.8432 25.3005 14.9997V16.9997C25.3061 17.1587 25.2787 17.3175 25.2205 17.4655C25.1621 17.6136 25.0733 17.7485 24.9607 17.861C24.8483 17.9734 24.7141 18.0615 24.5662 18.1198C24.4181 18.1781 24.2594 18.2055 24.1004 18.1999C23.8699 18.2018 23.6444 18.2686 23.45 18.3923C23.2555 18.516 23.0991 18.692 22.9998 18.9001C22.9412 19.1248 22.9388 19.3603 22.991 19.5866C23.0432 19.813 23.1494 20.0235 23.3005 20.1999C23.4188 20.3076 23.5133 20.4393 23.5779 20.5856C23.6425 20.7319 23.6755 20.8906 23.6755 21.0505C23.6755 21.2102 23.6424 21.3682 23.5779 21.5143C23.5133 21.6606 23.4187 21.7923 23.3005 21.9001L21.9002 23.3005C21.7924 23.4186 21.6607 23.5132 21.5144 23.5778C21.3683 23.6423 21.2103 23.6754 21.0505 23.6755C20.8906 23.6755 20.732 23.6424 20.5857 23.5778C20.4394 23.5132 20.3077 23.4187 20.2 23.3005C20.0471 23.1135 19.8366 22.9827 19.6013 22.9284C19.3661 22.8741 19.1194 22.8987 18.9002 22.9997C18.6921 23.099 18.5161 23.2554 18.3923 23.4499C18.2687 23.6443 18.2018 23.8698 18.2 24.1003C18.2055 24.2593 18.1782 24.418 18.1199 24.5661C18.0616 24.714 17.9735 24.8482 17.8611 24.9606C17.7485 25.0732 17.6137 25.1621 17.4656 25.2204C17.3176 25.2786 17.1587 25.3061 16.9998 25.3005H14.8796C14.7596 25.2943 14.6409 25.269 14.5281 25.2263C14.3777 25.1692 14.2404 25.0813 14.1257 24.9684C14.0112 24.8557 13.9216 24.7203 13.8621 24.571C13.8026 24.4215 13.7743 24.2611 13.78 24.1003C13.7782 23.8698 13.7114 23.6444 13.5877 23.4499C13.4639 23.2554 13.2879 23.099 13.0798 22.9997C12.8552 22.9412 12.6196 22.9387 12.3933 22.9909C12.167 23.0432 11.9564 23.1493 11.78 23.3005C11.6724 23.4185 11.5414 23.5132 11.3953 23.5778C11.249 23.6424 11.0904 23.6754 10.9304 23.6755C10.7705 23.6755 10.6119 23.6424 10.4656 23.5778C10.3192 23.5132 10.1876 23.4187 10.0798 23.3005L8.68043 21.9001C8.56217 21.7923 8.46773 21.6607 8.40309 21.5143C8.33855 21.3682 8.30451 21.2103 8.30445 21.0505C8.30445 20.8905 8.33846 20.732 8.40309 20.5856C8.46773 20.4393 8.56217 20.3077 8.68043 20.1999C8.86725 20.0471 8.99823 19.8365 9.0525 19.6013C9.10676 19.366 9.08127 19.1194 8.98024 18.9001C8.88095 18.6921 8.72538 18.516 8.53102 18.3923C8.33664 18.2686 8.11101 18.2018 7.88063 18.1999C7.72161 18.2055 7.56287 18.1781 7.41481 18.1198C7.2667 18.0615 7.13186 17.9736 7.0193 17.861C6.90673 17.7485 6.81884 17.6136 6.76051 17.4655C6.70222 17.3174 6.67484 17.1587 6.68043 16.9997V14.9997C6.67472 14.839 6.70199 14.6784 6.76149 14.529C6.82102 14.3796 6.91146 14.2443 7.02613 14.1315C7.14087 14.0187 7.27799 13.9308 7.42848 13.8737C7.5789 13.8167 7.73952 13.7921 7.90016 13.8005C8.13063 13.7986 8.35609 13.7318 8.55055 13.6081C8.74494 13.4844 8.90043 13.3082 8.99977 13.1003C9.09531 12.886 9.12582 12.648 9.08668 12.4167C9.04753 12.1855 8.94066 11.9713 8.78004 11.8005C8.66178 11.6927 8.56734 11.5611 8.5027 11.4147C8.43806 11.2684 8.40504 11.1099 8.40504 10.9499C8.40507 10.79 8.43808 10.6314 8.5027 10.485C8.5673 10.3389 8.66195 10.2079 8.78004 10.1003L10.1804 8.69989C10.2881 8.58181 10.419 8.48715 10.5652 8.42255C10.7115 8.35793 10.8701 8.32491 11.03 8.32489C11.19 8.32489 11.3485 8.35791 11.4949 8.42255C11.6412 8.48719 11.7729 8.58163 11.8806 8.69989C12.0734 8.85719 12.3004 8.96706 12.5437 9.01923C12.7871 9.07139 13.0396 9.06426 13.28 8.9997C13.4918 8.90323 13.6721 8.74909 13.7996 8.55438C13.927 8.35968 13.9961 8.13276 13.9998 7.90009C13.9943 7.74273 14.0218 7.58588 14.0789 7.43915C14.136 7.29246 14.2224 7.15881 14.3328 7.04657C14.4431 6.9344 14.5748 6.84537 14.7205 6.78583C14.8662 6.72626 15.023 6.69708 15.1804 6.69989H17.1804ZM16.0398 11.9597C14.9685 11.9598 13.9409 12.3857 13.1834 13.1433C12.4258 13.9008 11.9999 14.9284 11.9998 15.9997C12.005 17.082 12.4365 18.1192 13.2 18.8864C13.9633 19.6535 14.9977 20.0897 16.0798 20.1003L16.1004 20.0905C17.1659 20.0618 18.1783 19.6181 18.9216 18.8542C19.665 18.0903 20.0802 17.0656 20.0798 15.9997C20.0797 14.9284 19.6547 13.9008 18.8972 13.1433C18.1396 12.3856 17.1113 11.9597 16.0398 11.9597ZM16.1004 13.9997C16.6272 14.005 17.131 14.2181 17.5017 14.5925C17.8724 14.9669 18.0798 15.4728 18.0798 15.9997C18.1081 16.2846 18.0767 16.5726 17.9871 16.8444C17.8974 17.1163 17.7515 17.3666 17.5593 17.5788C17.3672 17.7908 17.1324 17.9599 16.8709 18.0759C16.6092 18.1919 16.326 18.2526 16.0398 18.2526C15.7537 18.2526 15.4703 18.1919 15.2088 18.0759C14.9474 17.9599 14.7132 17.7907 14.5213 17.5788C14.3291 17.3666 14.1832 17.1163 14.0935 16.8444C14.0039 16.5726 13.9715 16.2846 13.9998 15.9997C13.9995 15.7286 14.0552 15.4599 14.1619 15.2106C14.2686 14.9614 14.4246 14.7365 14.6209 14.5495C14.8173 14.3624 15.0501 14.2172 15.3045 14.1227C15.5587 14.0284 15.8295 13.9861 16.1004 13.9997Z"
                                                    fill="white"
                                                    fillOpacity="0.66"
                                                />
                                            </svg>
                                        </button>
                                        {/* 分隔线 */}
                                        <div className="bg-white/16 h-[16px] w-[4px] rounded-full" />
                                        {/* 发送按钮 */}
                                        <button
                                            ref={buttonRef}
                                            className="group/send disabled flex shrink-0 items-center justify-center transition-opacity"
                                        >
                                            {/* 未激活状态（有 disabled class 时显示） */}
                                            <svg
                                                width="35"
                                                height="32"
                                                viewBox="0 0 35 32"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="block group-[.disabled]/send:hidden"
                                            >
                                                <g clipPath="url(#send-active-clip)">
                                                    <path
                                                        d="M19 31C27.2843 31 34 24.2843 34 16C34 7.71573 27.2843 1 19 1C10.7157 1 4 7.71573 4 16C4 24.2843 10.7157 31 19 31Z"
                                                        fill="#724DF9"
                                                    />
                                                    <path
                                                        d="M26.1 17.3999L15.1 22.3999C14.8693 22.49 14.6202 22.5232 14.374 22.4965C14.1277 22.4699 13.8915 22.3842 13.6854 22.2468C13.4793 22.1094 13.3094 21.9243 13.19 21.7073C13.0707 21.4902 13.0055 21.2475 13 20.9999V17.8699L22.7 15.9999L13 14.1299V10.9999C13.0025 10.7442 13.0687 10.4931 13.1925 10.2694C13.3163 10.0456 13.4939 9.85626 13.7093 9.71833C13.9246 9.58039 14.1709 9.49825 14.4259 9.47932C14.6809 9.46039 14.9367 9.50527 15.17 9.60989L26.17 14.6099C26.4323 14.7405 26.652 14.9432 26.8031 15.1942C26.9543 15.4453 27.0306 15.7342 27.0233 16.0272C27.0159 16.3201 26.9252 16.6049 26.7616 16.848C26.5981 17.0912 26.3686 17.2826 26.1 17.3999Z"
                                                        fill="white"
                                                    />
                                                </g>
                                                <defs>
                                                    <clipPath id="send-active-clip">
                                                        <rect
                                                            width="30"
                                                            height="30"
                                                            fill="white"
                                                            transform="translate(4 1)"
                                                        />
                                                    </clipPath>
                                                </defs>
                                            </svg>
                                            {/* 激活状态（有 disabled class 时显示） */}
                                            <svg
                                                width="35"
                                                height="32"
                                                viewBox="0 0 35 32"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="hidden group-[.disabled]/send:block"
                                            >
                                                <g clipPath="url(#send-inactive-clip)">
                                                    <path
                                                        d="M19 31C27.2843 31 34 24.2843 34 16C34 7.71573 27.2843 1 19 1C10.7157 1 4 7.71573 4 16C4 24.2843 10.7157 31 19 31Z"
                                                        fill="#6A696C"
                                                    />
                                                    <path
                                                        d="M26.1 17.3999L15.1 22.3999C14.8693 22.49 14.6202 22.5232 14.374 22.4965C14.1277 22.4699 13.8915 22.3842 13.6854 22.2468C13.4793 22.1094 13.3094 21.9243 13.19 21.7073C13.0707 21.4902 13.0055 21.2475 13 20.9999V17.8699L22.7 15.9999L13 14.1299V10.9999C13.0025 10.7442 13.0687 10.4931 13.1925 10.2694C13.3163 10.0456 13.4939 9.85626 13.7093 9.71833C13.9246 9.58039 14.1709 9.49825 14.4259 9.47932C14.6809 9.46039 14.9367 9.50527 15.17 9.60989L26.17 14.6099C26.4323 14.7405 26.652 14.9432 26.8031 15.1942C26.9543 15.4453 27.0306 15.7342 27.0233 16.0272C27.0159 16.3201 26.9252 16.6049 26.7616 16.848C26.5981 17.0912 26.3686 17.2826 26.1 17.3999Z"
                                                        fill="#21252D"
                                                    />
                                                </g>
                                                <defs>
                                                    <clipPath id="send-inactive-clip">
                                                        <rect
                                                            width="30"
                                                            height="30"
                                                            fill="white"
                                                            transform="translate(4 1)"
                                                        />
                                                    </clipPath>
                                                </defs>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default ThreeDGenerate;
